/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef } from 'react';

import { delay } from '@libs/utils/helpers/delay';
import { getApp } from '@libs/utils/helpers/getApplication';

import { useCreditTrack } from '../../../hooks/useCreditTrack';
import { useSH } from '../';

type UseSortingHat = {
  user?: any;
  accessToken?: string;
  immediate?: boolean;
  decideDelayInSeconds?: number;
  offersResponseTimeMs?: number;
  forceCall?: boolean;
};

type SHRequestParams = {
  payload?: {
    documento?: string;
    [key: string]: any;
  };
  forceCall?: boolean;
  ignoreToken?: boolean;
};

export const useSortingHat = ({ immediate, decideDelayInSeconds, offersResponseTimeMs, forceCall, user, accessToken }: UseSortingHat) => {
  const {
    callSHTrack,
    callSHDecision,
    callSHOffers,
    decisionsList: shDecisionList,
    trackDone,
    decideDone,
    singleCallMode,
    SHLoading,
    SHError,
    isEndnodeList,
    resetContext: resetSHContext,
  } = useSH();

  const { decisionEnrichedEvent, decisionRequestedEvent, decisionOutcomeReceivedEvent, productPreApprovedEvent } = useCreditTrack();

  const hasCalledWithEndnodeRef = useRef(false);

  useEffect(() => {
    if (!immediate) return;
    if (!user) {
      return console.warn('[SH_TRACK] User not found');
    }

    if (singleCallMode) {
      if (!accessToken) return;
      if (!decideDone || forceCall) {
        shOffers({ forceCall: forceCall });
      }
      return;
    }

    if (!trackDone || forceCall) {
      shTrack({ forceCall: forceCall });
    }

    if (trackDone) {
      shDecision({ forceCall: forceCall });
    }
  }, [trackDone, decideDone, user, accessToken, immediate, singleCallMode]);

  useEffect(() => {
    // TODO: remover "documento" após rollout de authProvider
    if (!(user?.documento || user?.document)) {
      return console.warn('[SH][RETRY_DECIDE] Document not found');
    }
    handleEndnodeList();
  }, [isEndnodeList, user]);

  const shTrack = useCallback(
    async (customPayload?: SHRequestParams) => {
      try {
        const safePayload = {
          ...user,
          ...customPayload?.payload,
        };
        callSHTrack({
          user: safePayload,
          decisionEnrichedEvent,
          forceCall: customPayload?.forceCall || false,
        });
      } catch (error) {
        console.error('[SH][TRACK]: ', error);
      }
    },
    [user]
  );

  const shDecision = useCallback(
    async (customPayload?: SHRequestParams) => {
      try {
        if (!trackDone) {
          return console.warn('[SH_DECIDE] Track not done yet');
        }

        if (decideDelayInSeconds) {
          const milliseconds = decideDelayInSeconds * 1000;
          if (milliseconds > 0) {
            await delay(milliseconds);
          }
        }

        const safePayload = {
          ...user,
          ...customPayload?.payload,
        };

        const result = await callSHDecision({
          user: safePayload,
          decisionRequestedEvent,
          decisionOutcomeReceivedEvent,
          productPreApprovedEvent,
          forceCall: customPayload?.forceCall || false,
          token: accessToken,
          ignoreToken: customPayload?.ignoreToken,
        });

        return result;
      } catch (error) {
        console.error('[SH][DECIDE] ', error);
      }
    },
    [user, trackDone, decideDelayInSeconds, accessToken]
  );

  const shOffers = useCallback(
    async (customPayload?: SHRequestParams) => {
      try {
        const safePayload = {
          ...user,
          ...customPayload?.payload,
        };

        return await callSHOffers({
          user: safePayload,
          decisionEnrichedEvent,
          decisionRequestedEvent,
          decisionOutcomeReceivedEvent,
          productPreApprovedEvent,
          forceCall: customPayload?.forceCall || false,
          token: accessToken,
          ignoreToken: customPayload?.ignoreToken,
          delayTime: decideDelayInSeconds ? decideDelayInSeconds * 1000 : undefined,
          withResponseTimeMs: offersResponseTimeMs,
        });
      } catch (error) {
        console.error('[SH][OFFERS] ', error);
      }
    },
    [user, accessToken, decideDelayInSeconds, offersResponseTimeMs]
  );

  const retryDecide = useCallback(
    async (ignoreToken = false) => {
      if (shDecisionList.length === 0) {
        if (singleCallMode) {
          await delay(1000);
          await shOffers({ forceCall: true, ignoreToken });
          return;
        }
        if (trackDone) {
          await delay(1000);
          await shDecision({ forceCall: true, ignoreToken });
        }
      }
    },
    [shDecisionList, trackDone, singleCallMode]
  );

  async function handleEndnodeList() {
    if (isEndnodeList && hasCalledWithEndnodeRef.current) {
      return;
    }

    if (isEndnodeList) {
      console.log('[SH][RETRY_DECIDE] endnode list, retrying');
      if (singleCallMode) {
        await shOffers({ forceCall: true });
      } else {
        await shTrack({ forceCall: true });
        await delay(1000);
        await shDecision({ forceCall: true });
      }
      hasCalledWithEndnodeRef.current = true;
    }
  }

  return {
    shTrack,
    shDecision,
    shOffers,
    shDecisionList,
    decideDone,
    SHLoading,
    trackDone,
    retryDecide,
    resetSHContext,
    SHError,
    singleCallMode,
  };
};
